import { useQuery } from '@tanstack/react-query';
import type { TdsRateOption, TdsSectionMaster } from '@fpc/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

/**
 * The tenant's active TDS sections and their rates (Settings → TDS Sections),
 * which every TDS section and rate picker offers. Sections a newer code has
 * replaced are read too, so a code recorded before the change (194IB) can be
 * shown with the one now in force (194IC).
 */
export function useTdsSections() {
  const { can } = useAuth();
  const query = useQuery({
    queryKey: ['settings-tds-sections'],
    queryFn: () => api.settings.tdsSections({ pageSize: 100, includeReplaced: 'true' }),
    enabled: can('tds_section:read'),
    // Read again whenever a TDS picker appears or the tab regains focus, so a
    // section or rate just added in Settings (here or in another tab) is offered.
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const all: TdsSectionMaster[] = query.data?.items ?? [];
  const sections: TdsSectionMaster[] = all
    .filter((section) => section.active && !section.replacedBy)
    .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
  const ratesFor = (code: string): TdsRateOption[] =>
    sections.find((section) => section.code === code)?.rates ?? [];

  /** The code in force for one since replaced (194IB → 194IC), else undefined. */
  const successorOf = (code: string): string | undefined => {
    let at = all.find((section) => section.code === code);
    if (!at?.replacedBy) return undefined;
    for (let step = 0; at?.replacedBy && step < 20; step++) {
      const next: string = at.replacedBy.code;
      at = all.find((section) => section.code === next) ?? {
        ...at,
        code: next,
        replacedBy: undefined,
      };
    }
    return at?.code;
  };
  /** `code` itself, or the code that has since replaced it. */
  const currentCodeFor = (code: string): string => successorOf(code) ?? code;

  return { sections, all, ratesFor, successorOf, currentCodeFor, loaded: query.isSuccess };
}
